import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { DateField } from '../../Booking/Details';
import {
  AttendanceEventType,
  GetTechnicianAttendanceCalendar,
  IAttendanceCalendarDay,
  IAttendanceCalendarSummary,
  IAttendanceDeductionPreview,
  IAttendanceSpanEvent,
} from '../../../services/technician';
import {
  ATTENDANCE_TYPE_COLORS,
  formatAttendanceClock,
  formatAttendanceDateTime,
  formatWorkingMinutes,
  getAttendanceEventLabel,
  resolveDayDisplayStatus,
} from '../../../utils/attendanceDisplay';
import { AttendanceTimeBlock } from './AttendanceTimeBlock';
import { AttendanceStatusChip } from './AttendanceStatusChip';
import { MonthlyConversionSummary } from './MonthlyConversionSummary';
import { EditAttendanceDialog, AttendanceEditRecord } from './EditAttendanceDialog';
import { PrimaryButton } from '../../../components/atoms/Button';

const EVENT_COLORS: Record<AttendanceEventType, string> = {
  CHECK_IN: ATTENDANCE_TYPE_COLORS.CHECK_IN,
  CHECK_OUT: ATTENDANCE_TYPE_COLORS.CHECK_OUT,
  PRESENT: ATTENDANCE_TYPE_COLORS.PRESENT,
  ABSENT: ATTENDANCE_TYPE_COLORS.ABSENT,
  LEAVE: ATTENDANCE_TYPE_COLORS.LEAVE,
  LEAVE_NO_INFO: ATTENDANCE_TYPE_COLORS.LEAVE_NO_INFO,
  HOLIDAY: ATTENDANCE_TYPE_COLORS.HOLIDAY,
  SUNDAY: ATTENDANCE_TYPE_COLORS.SUNDAY,
  LATE_COMING: ATTENDANCE_TYPE_COLORS.LATE_COMING,
  EARLY_GOING: ATTENDANCE_TYPE_COLORS.EARLY_GOING,
  HALF_DAY: ATTENDANCE_TYPE_COLORS.HALF_DAY,
  LATE_CHECK_IN: ATTENDANCE_TYPE_COLORS.LATE_CHECK_IN,
  WEEKLY_OFF: ATTENDANCE_TYPE_COLORS.WEEKLY_OFF,
};

const SUMMARY_ITEMS: {
  key: string;
  legacyKey?: keyof IAttendanceCalendarSummary;
  label: string;
  color: string;
}[] = [
  { key: 'presentDays', label: 'Present', color: EVENT_COLORS.PRESENT },
  {
    key: 'lateComing',
    label: 'Late Check-In',
    color: EVENT_COLORS.LATE_CHECK_IN,
  },
  {
    key: 'halfDayWork',
    label: 'Half Day',
    color: EVENT_COLORS.HALF_DAY,
  },
  { key: 'absentDays', label: 'Absent', color: EVENT_COLORS.ABSENT },
  { key: 'leavesTaken', label: 'Leave', color: EVENT_COLORS.LEAVE },
  {
    key: 'leavesWithoutInfo',
    label: 'Leave (no info)',
    color: EVENT_COLORS.LEAVE_NO_INFO,
  },
  { key: 'holiday', label: 'Holiday', color: EVENT_COLORS.HOLIDAY },
  {
    key: 'weeklyOff',
    legacyKey: 'sunday',
    label: 'Weekly Off',
    color: EVENT_COLORS.WEEKLY_OFF,
  },
];

const defaultSummary = (): IAttendanceCalendarSummary => ({
  presentDays: 0,
  absentDays: 0,
  leavesTaken: 0,
  leavesWithoutInfo: 0,
  holiday: 0,
  lateComing: 0,
  earlyGoing: 0,
  halfDay: 0,
  sunday: 0,
});

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const dateInputSx = {
  '& .MuiInputBase-root': {
    height: 34,
    fontSize: '0.8125rem',
    color: '#f3f4f6',
    backgroundColor: '#111',
    borderRadius: '8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#333',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#555',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6b7280',
    borderWidth: 1,
  },
  '& input[type="date"]': {
    py: 0.75,
    px: 1,
  },
};

const sidebarActionSx = {
  flex: 1,
  minWidth: 0,
  height: 32,
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'none' as const,
  borderRadius: '8px',
  boxShadow: 'none',
};

const buildWeeksForRange = (rangeStart: Dayjs, rangeEnd: Dayjs) => {
  const start = rangeStart.startOf('day');
  const end = rangeEnd.startOf('day');
  const day = start.day();
  const offset = day === 0 ? -6 : 1 - day;
  let cursor = start.add(offset, 'day');

  const weeks: Dayjs[][] = [];

  while (cursor.isBefore(end) || cursor.isSame(end, 'day') || weeks.length === 0) {
    const week: Dayjs[] = [];
    for (let i = 0; i < 7; i += 1) {
      week.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    weeks.push(week);
    if (cursor.isAfter(end.add(6, 'day'))) break;
  }

  return weeks;
};

type RangeMode = 'month' | 'custom';

interface CustomRange {
  start: string;
  end: string;
}

interface DetailedAttendanceCalendarProps {
  technicianId: string;
  /** Sync month with compact calendar when provided */
  month?: Dayjs;
  onMonthChange?: (month: Dayjs) => void;
  onAttendanceSaved?: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const DayMarks = ({ record }: { record?: IAttendanceCalendarDay }) => {
  const hasTimes = Boolean(record?.checkInTime || record?.checkOutTime);
  const hasEvents = Boolean(record?.events?.length);
  const computedStatus =
    record?.computedStatus && record.computedStatus !== 'FUTURE'
      ? record.computedStatus
      : null;
  const isPresent = record?.status === 'PRESENT' || hasTimes;

  if (!record || (!hasEvents && !isPresent && !computedStatus)) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
      {computedStatus && (
        <Box sx={{ alignSelf: 'flex-start' }}>
          <AttendanceStatusChip
            status={resolveDayDisplayStatus(computedStatus, record.events)}
            withHint
          />
        </Box>
      )}
      {record.checkInTime && (
        <Typography
          variant="caption"
          sx={{ color: EVENT_COLORS.CHECK_IN, fontSize: '0.62rem', fontWeight: 600 }}
        >
          In {formatAttendanceClock(record.checkInTime)}
        </Typography>
      )}
      {record.checkOutTime && (
        <Typography
          variant="caption"
          sx={{ color: EVENT_COLORS.CHECK_OUT, fontSize: '0.62rem', fontWeight: 600 }}
        >
          Out {formatAttendanceClock(record.checkOutTime)}
        </Typography>
      )}
      {/* Legacy responses without computedStatus keep the event pills */}
      {!computedStatus &&
        record.events
          ?.filter((e) => !['CHECK_IN', 'CHECK_OUT'].includes(e.type))
          .slice(0, 2)
          .map((event, index) => (
            <Box
              key={`${event.type}-${index}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
                maxWidth: '100%',
                fontSize: '0.6rem',
                lineHeight: 1.2,
                px: 0.65,
                py: 0.22,
                borderRadius: '999px',
                color: '#f9fafb',
                backgroundColor: EVENT_COLORS[event.type] || '#374151',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getAttendanceEventLabel(event.type, event.label)}
            </Box>
          ))}
      {!computedStatus && isPresent && !hasTimes && !hasEvents && (
        <Typography
          variant="caption"
          sx={{ color: EVENT_COLORS.PRESENT, fontSize: '0.62rem', fontWeight: 600 }}
        >
          Present
        </Typography>
      )}
    </Box>
  );
};

export const DetailedAttendanceCalendar = ({
  technicianId,
  month: controlledMonth,
  onMonthChange,
  onAttendanceSaved,
  onSuccess,
  onError,
}: DetailedAttendanceCalendarProps) => {
  const [internalMonth, setInternalMonth] = useState<Dayjs>(
    () => controlledMonth?.startOf('month') || dayjs().startOf('month'),
  );
  const [initialLoad, setInitialLoad] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [days, setDays] = useState<IAttendanceCalendarDay[]>([]);
  const [spanEvents, setSpanEvents] = useState<IAttendanceSpanEvent[]>([]);
  const [summary, setSummary] = useState(defaultSummary());
  const [deductionPreview, setDeductionPreview] =
    useState<IAttendanceDeductionPreview | null>(null);
  const [rangeLabel, setRangeLabel] = useState('');
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [rangeMode, setRangeMode] = useState<RangeMode>('month');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [rangeError, setRangeError] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{
    date: Dayjs;
    record: AttendanceEditRecord | null;
  } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const requestId = useRef(0);

  const activeMonth = controlledMonth ?? internalMonth;
  const monthKey = activeMonth.format('YYYY-MM');
  const calendarMonth = useMemo(
    () => activeMonth.startOf('month'),
    [monthKey],
  );

  const setVisibleMonth = (next: Dayjs) => {
    const normalized = next.startOf('month');
    const nextKey = normalized.format('YYYY-MM');
    if (nextKey === monthKey && rangeMode === 'month') return;

    setRangeMode('month');
    setCustomRange(null);
    setRangeError('');

    if (onMonthChange) {
      onMonthChange(normalized);
    } else {
      setInternalMonth(normalized);
    }
  };

  const fetchKey =
    rangeMode === 'custom' && customRange
      ? `custom_${customRange.start}_${customRange.end}`
      : `month_${monthKey}_${reloadToken}`;

  useEffect(() => {
    const currentRequest = ++requestId.current;
    const [yearStr, monthStr] = monthKey.split('-');
    const month = Number(monthStr);
    const year = Number(yearStr);

    const load = async () => {
      setFetching(true);

      try {
        const response = await GetTechnicianAttendanceCalendar(
          rangeMode === 'custom' && customRange
            ? {
                technicianId,
                startDate: customRange.start,
                endDate: customRange.end,
              }
            : {
                technicianId,
                month,
                year,
              },
        );

        if (currentRequest !== requestId.current) return;

        setDays(response.data);
        setSpanEvents(response.spanEvents);
        setSummary(response.summary);
        setDeductionPreview(response.deductionPreview ?? null);
        setRangeLabel(response.rangeLabel);
        setDraftStartDate(response.startDate);
        setDraftEndDate(response.endDate);
        setSelectedKey(null);
      } catch (err) {
        if (currentRequest !== requestId.current) return;
        console.error('Failed to load attendance calendar:', err);
      } finally {
        if (currentRequest === requestId.current) {
          setFetching(false);
          setInitialLoad(false);
        }
      }
    };

    load();
  }, [technicianId, fetchKey, rangeMode, customRange?.start, customRange?.end]);

  const applyCustomRange = () => {
    if (!draftStartDate || !draftEndDate) {
      setRangeError('Select both start and end dates.');
      return;
    }

    if (dayjs(draftStartDate).isAfter(dayjs(draftEndDate), 'day')) {
      setRangeError('Start date must be on or before end date.');
      return;
    }

    setRangeError('');
    setRangeMode('custom');
    setCustomRange({
      start: draftStartDate,
      end: draftEndDate,
    });
  };

  const resetToMonthView = () => {
    setRangeMode('month');
    setCustomRange(null);
    setRangeError('');
    setDraftStartDate(calendarMonth.startOf('month').format('YYYY-MM-DD'));
    setDraftEndDate(calendarMonth.endOf('month').format('YYYY-MM-DD'));
  };

  const dayMap = useMemo(
    () => new Map(days.map((day) => [day.date, day])),
    [days],
  );

  const visibleRange = useMemo(() => {
    if (rangeMode === 'custom' && customRange) {
      return {
        start: dayjs(customRange.start),
        end: dayjs(customRange.end),
      };
    }

    return {
      start: calendarMonth.startOf('month'),
      end: calendarMonth.endOf('month'),
    };
  }, [rangeMode, customRange, monthKey]);

  const weeks = useMemo(
    () => buildWeeksForRange(visibleRange.start, visibleRange.end),
    [visibleRange.start, visibleRange.end],
  );
  const selectedDay = selectedKey ? dayMap.get(selectedKey) : undefined;

  const openDayEditor = (key: string) => {
    const record = dayMap.get(key);
    setSelectedKey(key);
    setEditTarget({
      date: dayjs(key),
      record: record ?? { status: 'ABSENT' },
    });
    setEditOpen(true);
  };

  const closeDayEditor = () => {
    setEditOpen(false);
    setEditTarget(null);
  };

  const handleAttendanceSaved = () => {
    setReloadToken((value) => value + 1);
    onAttendanceSaved?.();
  };

  const notifySuccess = (message: string) => {
    if (onSuccess) onSuccess(message);
  };

  const notifyError = (message: string) => {
    if (onError) onError(message);
  };

  const getSpanSegmentsForWeek = (week: Dayjs[]) => {
    const weekStart = week[0].format('YYYY-MM-DD');
    const weekEnd = week[6].format('YYYY-MM-DD');

    return spanEvents
      .map((event) => {
        if (event.endDate < weekStart || event.startDate > weekEnd) return null;

        let startCol = 7;
        let endCol = -1;

        week.forEach((day, index) => {
          const key = day.format('YYYY-MM-DD');
          if (key >= event.startDate && key <= event.endDate) {
            startCol = Math.min(startCol, index);
            endCol = Math.max(endCol, index);
          }
        });

        if (endCol === -1) return null;

        // Leave and generic holiday spans duplicate the day status chips
        // (same dedup rule as the legacy event pills above): skip the pill
        // when every covered day already renders a chip via computedStatus.
        // Named holidays (e.g. "Diwali") keep their pill — the chip can't
        // show the name.
        if (
          event.type === 'LEAVE' ||
          event.type === 'HALF_DAY' ||
          (event.type === 'HOLIDAY' && (!event.label || event.label === 'Holiday'))
        ) {
          let allDaysHaveChip = true;
          for (let i = startCol; i <= endCol; i += 1) {
            const dayRecord = dayMap.get(week[i].format('YYYY-MM-DD'));
            if (!dayRecord?.computedStatus || dayRecord.computedStatus === 'FUTURE') {
              allDaysHaveChip = false;
              break;
            }
          }
          if (allDaysHaveChip) return null;
        }

        if (event.startDate < weekStart) startCol = 0;
        if (event.endDate > weekEnd) endCol = 6;

        return {
          ...event,
          startCol,
          span: endCol - startCol + 1,
          color: EVENT_COLORS[event.type] || EVENT_COLORS.LEAVE,
        };
      })
      .filter(Boolean) as (IAttendanceSpanEvent & {
      startCol: number;
      span: number;
      color: string;
    })[];
  };

  if (initialLoad && fetching && days.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: '#E31E25' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 0,
        minHeight: 520,
        backgroundColor: '#111',
        borderRadius: 2,
        border: '1px solid #2a2a2a',
        overflow: 'hidden',
      }}
    >
      {fetching && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={28} sx={{ color: '#E31E25' }} />
        </Box>
      )}

      <Box
        sx={{
          width: { xs: '100%', lg: 248 },
          flexShrink: 0,
          p: 2,
          borderRight: { lg: '1px solid #2a2a2a' },
          borderBottom: { xs: '1px solid #2a2a2a', lg: 'none' },
          backgroundColor: '#141414',
          opacity: fetching ? 0.85 : 1,
          transition: 'opacity 0.15s ease',
          overflowY: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <IconButton
            size="small"
            disabled={fetching || rangeMode === 'custom'}
            onClick={() => setVisibleMonth(calendarMonth.subtract(1, 'month'))}
            sx={{ color: '#9ca3af' }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={700} color="#fff" sx={{ flex: 1 }}>
            {calendarMonth.format('MMM YYYY')}
          </Typography>
          <IconButton
            size="small"
            disabled={fetching || rangeMode === 'custom'}
            onClick={() => setVisibleMonth(calendarMonth.add(1, 'month'))}
            sx={{ color: '#9ca3af' }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>

        <Typography
          variant="caption"
          color="#9ca3af"
          sx={{
            display: 'block',
            mb: 1.25,
            lineHeight: 1.45,
            fontSize: '0.72rem',
          }}
        >
          {rangeLabel}
        </Typography>

        <Box
          sx={{
            p: 1.25,
            mb: 1.5,
            borderRadius: 1.5,
            border: '1px solid #2a2a2a',
            backgroundColor: '#181818',
          }}
        >
          <Typography
            variant="caption"
            color="#d1d5db"
            sx={{ display: 'block', mb: 1, fontWeight: 600, fontSize: '0.72rem' }}
          >
            Filter by date range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
              <Typography
                variant="caption"
                color="#6b7280"
                sx={{ display: 'block', mb: 0.5, fontSize: '0.68rem' }}
              >
                From
              </Typography>
              <DateField
                type="date"
                size="small"
                fullWidth
                value={draftStartDate}
                disabled={fetching}
                onChange={(event) => {
                  setDraftStartDate(event.target.value);
                  setRangeError('');
                }}
                sx={dateInputSx}
              />
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="#6b7280"
                sx={{ display: 'block', mb: 0.5, fontSize: '0.68rem' }}
              >
                To
              </Typography>
              <DateField
                type="date"
                size="small"
                fullWidth
                value={draftEndDate}
                disabled={fetching}
                onChange={(event) => {
                  setDraftEndDate(event.target.value);
                  setRangeError('');
                }}
                inputProps={{ min: draftStartDate || undefined }}
                sx={dateInputSx}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25 }}>
            <Box
              component="button"
              type="button"
              disabled={fetching}
              onClick={applyCustomRange}
              sx={{
                ...sidebarActionSx,
                border: 'none',
                cursor: fetching ? 'not-allowed' : 'pointer',
                color: '#fff',
                backgroundColor: fetching ? '#7f1d21' : '#E31E25',
                opacity: fetching ? 0.7 : 1,
                '&:hover': {
                  backgroundColor: fetching ? '#7f1d21' : '#c91920',
                },
              }}
            >
              Apply
            </Box>
            {rangeMode === 'custom' && (
              <Box
                component="button"
                type="button"
                disabled={fetching}
                onClick={resetToMonthView}
                sx={{
                  ...sidebarActionSx,
                  border: '1px solid #404040',
                  cursor: fetching ? 'not-allowed' : 'pointer',
                  color: '#d1d5db',
                  backgroundColor: 'transparent',
                  opacity: fetching ? 0.7 : 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderColor: '#555',
                  },
                }}
              >
                Month view
              </Box>
            )}
          </Box>

          {rangeError && (
            <Typography
              variant="caption"
              color="#f87171"
              sx={{ display: 'block', mt: 0.75, lineHeight: 1.35, fontSize: '0.68rem' }}
            >
              {rangeError}
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: '#2a2a2a', mb: 1.25 }} />

        {SUMMARY_ITEMS.map((item) => {
          const summaryRecord = summary as unknown as Record<string, number | undefined>;
          const value =
            summaryRecord[item.key] ??
            (item.legacyKey ? summaryRecord[item.legacyKey] : undefined) ??
            0;
          return (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.45,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                  }}
                />
                <Typography variant="body2" color="#9ca3af" sx={{ fontSize: '0.875rem' }}>
                  {item.label}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} color="#f3f4f6" sx={{ fontSize: '0.875rem' }}>
                {value}
              </Typography>
            </Box>
          );
        })}

        {deductionPreview && rangeMode === 'custom' && (
          <Typography
            variant="caption"
            color="#6b7280"
            sx={{ display: 'block', mt: 1.25, lineHeight: 1.4, fontSize: '0.68rem' }}
          >
            Switch to month view for the payroll conversion summary.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          opacity: fetching ? 0.85 : 1,
          transition: 'opacity 0.15s ease',
        }}
      >
        <Box sx={{ flex: 1, p: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              backgroundColor: '#1a1a1a',
              borderRadius: '8px 8px 0 0',
              border: '1px solid #2a2a2a',
              borderBottom: 'none',
            }}
          >
            {WEEKDAYS.map((label) => (
              <Box key={label} sx={{ py: 0.85, textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="#9ca3af"
                  sx={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {weeks.map((week, weekIndex) => {
            const segments = getSpanSegmentsForWeek(week);

            return (
              <Box
                key={weekIndex}
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  borderLeft: '1px solid #2a2a2a',
                  borderRight: '1px solid #2a2a2a',
                  borderBottom: '1px solid #2a2a2a',
                  minHeight: 100,
                }}
              >
                {week.map((day) => {
                  const key = day.format('YYYY-MM-DD');
                  const inRange =
                    !day.isBefore(visibleRange.start, 'day') &&
                    !day.isAfter(visibleRange.end, 'day');
                  const record = dayMap.get(key);
                  // Weekly-off tint: engine status wins; fall back to Sunday
                  // only when there's no record (legacy responses).
                  const isWeeklyOff =
                    record?.computedStatus === 'WEEKLY_OFF' ||
                    record?.status === 'WEEKLY_OFF' ||
                    record?.status === 'SUNDAY' ||
                    (!record && day.day() === 0);
                  const isSelected = selectedKey === key;

                  return (
                    <Tooltip
                      key={key}
                      title={
                        inRange
                          ? record?.checkInTime || record?.checkOutTime
                            ? `In: ${formatAttendanceDateTime(record.checkInTime, key)} · Out: ${formatAttendanceDateTime(record.checkOutTime, key)} — Click to edit`
                            : `${record?.status || 'No record'} — Click to edit`
                          : ''
                      }
                      disableHoverListener={!inRange}
                    >
                      <Box
                        onClick={() => inRange && !fetching && openDayEditor(key)}
                        sx={{
                          position: 'relative',
                          borderRight: '1px solid #2a2a2a',
                          backgroundColor: inRange
                            ? isSelected
                              ? 'rgba(227, 30, 37, 0.12)'
                              : isWeeklyOff
                                ? 'rgba(59, 130, 246, 0.06)'
                                : '#161616'
                            : '#121212',
                          p: 0.75,
                          pt: 3,
                          minHeight: 92,
                          overflow: 'hidden',
                          cursor: inRange && !fetching ? 'pointer' : 'default',
                          outline: isSelected ? '1px solid #E31E25' : 'none',
                          outlineOffset: -1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            top: 5,
                            right: 7,
                            color: inRange ? '#f9fafb' : '#4b5563',
                            fontWeight: 700,
                            fontSize: '1rem',
                            lineHeight: 1,
                          }}
                        >
                          {day.format('D')}
                        </Typography>

                        {inRange && <DayMarks record={record} />}
                      </Box>
                    </Tooltip>
                  );
                })}

                {segments.map((segment, index) => (
                  <Box
                    key={`${segment.id}-${weekIndex}`}
                    sx={{
                      position: 'absolute',
                      left: `calc(${(segment.startCol / 7) * 100}% + 4px)`,
                      width: 'max-content',
                      maxWidth: `calc(${(segment.span / 7) * 100}% - 8px)`,
                      bottom: 6 + index * 20,
                      minHeight: 16,
                      borderRadius: '999px',
                      backgroundColor: segment.color,
                      color: '#fff',
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 0.75,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      zIndex: 2,
                      opacity: 0.92,
                    }}
                  >
                    {getAttendanceEventLabel(segment.type, segment.label)}
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>

        {deductionPreview && rangeMode === 'month' && (
          <MonthlyConversionSummary preview={deductionPreview} />
        )}

        {selectedDay && (
          <Box
            sx={{
              borderTop: '1px solid #2a2a2a',
              backgroundColor: '#141414',
              px: 2,
              py: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" fontWeight={700} color="#f3f4f6">
                {dayjs(selectedDay.date).format('dddd, DD MMM YYYY')}
              </Typography>
              <AttendanceStatusChip
                status={resolveDayDisplayStatus(
                  selectedDay.computedStatus ?? selectedDay.status,
                  selectedDay.events,
                )}
                withHint
              />
              {typeof selectedDay.workedMinutes === 'number' &&
                selectedDay.workedMinutes > 0 && (
                  <Typography variant="caption" color="#9ca3af">
                    Worked {formatWorkingMinutes(selectedDay.workedMinutes)}
                  </Typography>
                )}
            </Box>

            {selectedDay.checkInTime || selectedDay.checkOutTime ? (
              <AttendanceTimeBlock
                attendanceDate={selectedDay.date}
                checkInTime={selectedDay.checkInTime}
                checkOutTime={selectedDay.checkOutTime}
              />
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedDay.events.map((event, index) => (
                  <Box
                    key={`${event.type}-${index}`}
                    sx={{
                      fontSize: '0.72rem',
                      px: 1,
                      py: 0.35,
                      borderRadius: 1,
                      backgroundColor: EVENT_COLORS[event.type] || '#374151',
                      color: '#fff',
                    }}
                  >
                    {getAttendanceEventLabel(event.type, event.label)}
                  </Box>
                ))}
              </Box>
            )}

            {selectedDay.description?.trim() && (
              <Typography variant="caption" color="#9ca3af" sx={{ display: 'block', mt: 0.75 }}>
                {selectedDay.description}
              </Typography>
            )}

            <Box sx={{ mt: 1.25 }}>
              <PrimaryButton
                title={
                  selectedDay.checkInTime ||
                  selectedDay.checkOutTime ||
                  selectedDay.status !== 'No record'
                    ? 'Edit Attendance'
                    : 'Mark Attendance'
                }
                onClick={() => selectedKey && openDayEditor(selectedKey)}
              />
            </Box>
          </Box>
        )}
      </Box>

      <EditAttendanceDialog
        open={editOpen}
        onClose={closeDayEditor}
        technicianId={technicianId}
        date={editTarget?.date ?? null}
        record={editTarget?.record}
        onSaved={handleAttendanceSaved}
        onSuccess={notifySuccess}
        onError={notifyError}
      />
    </Box>
  );
};
